import { api } from './api';
import { 
  SalesOrder, 
  CreateSalesOrderRequest, 
  SalesOrderFilters, 
  SalesOrderStatus,
  SalesDashboardData,
  SalesDashboardRange,
  SalesOrderQueryParams,
  PaginatedSalesOrders,
  SalesOrderSummary
} from '../types/sales.types';

export const salesService = {
  // Get all sales orders with optional filters
  getAll: async (filters?: SalesOrderFilters): Promise<SalesOrder[]> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.customerId) params.append('customerId', filters.customerId);
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    
    const queryString = params.toString();
    const url = queryString ? `/sales?${queryString}` : '/sales';
    
    const response = await api.get(url);
    return response.data;
  },

  // Get one page of sales orders, filtered on the server
  getPage: async (query: SalesOrderQueryParams): Promise<PaginatedSalesOrders> => {
    const params = {
      page: query.page,
      limit: query.limit,
      ...(query.search ? { search: query.search } : {}),
      ...(query.status ? { status: query.status } : {}),
      ...(query.sortBy ? { sortBy: query.sortBy } : {}),
      ...(query.sortOrder ? { sortOrder: query.sortOrder } : {}),
    };
    const response = await api.get<PaginatedSalesOrders>('/sales/paginated', { params });
    return response.data;
  },

  // Get the totals shown above the sales order list
  getSummary: async (): Promise<SalesOrderSummary> => {
    const response = await api.get<SalesOrderSummary>('/sales/summary');
    return response.data;
  },

  // Get sales order by ID
  getById: async (id: string): Promise<SalesOrder> => {
    const response = await api.get(`/sales/${id}`);
    return response.data;
  },

  // Create new sales order
  create: async (data: CreateSalesOrderRequest): Promise<SalesOrder> => {
    const response = await api.post('/sales', data);
    return response.data;
  },

  // Update sales order status
  updateStatus: async (id: string, status: SalesOrderStatus): Promise<SalesOrder> => {
    const response = await api.patch(`/sales/${id}/status`, { status });
    return response.data;
  },

  // Get sales dashboard data for a reporting period
  getDashboard: async (range: SalesDashboardRange = '30d'): Promise<SalesDashboardData> => {
    const response = await api.get<SalesDashboardData>('/sales/dashboard', { params: { range } });
    return response.data;
  },

  // Generate sales report
  generateReport: async (filters?: SalesOrderFilters): Promise<Blob> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    
    const queryString = params.toString();
    const url = queryString ? `/sales/report?${queryString}` : '/sales/report';
    
    const response = await api.get(url, { responseType: 'blob' });
    return response.data;
  },

  // Quotes Management Methods
  // Get all quotes
  getQuotes: async (query?: { sortBy?: string; sortOrder?: string }): Promise<SalesOrder[]> => {
    const params = new URLSearchParams();
    if (query?.sortBy) params.append('sortBy', query.sortBy);
    if (query?.sortOrder) params.append('sortOrder', query.sortOrder);
    
    const queryString = params.toString();
    const url = queryString ? `/sales/quotes?${queryString}` : '/sales/quotes';
    
    const response = await api.get(url);
    return response.data;
  },

  // Create new quote
  createQuote: async (data: CreateSalesOrderRequest): Promise<SalesOrder> => {
    const response = await api.post('/sales/quotes', data);
    return response.data;
  },

  // Convert quote to order
  convertQuoteToOrder: async (id: string): Promise<SalesOrder> => {
    const response = await api.post(`/sales/${id}/convert-to-order`);
    return response.data;
  },

  // Update quote
  updateQuote: async (id: string, data: Partial<CreateSalesOrderRequest>): Promise<SalesOrder> => {
    const response = await api.patch(`/sales/quotes/${id}`, data);
    return response.data;
  },

  // Delete quote
  deleteQuote: async (id: string): Promise<void> => {
    await api.post(`/sales/quotes/${id}/delete`);
  },
};
